var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { useToast } from '@pocketlaw/tetris';
import { assistantCache, AssistantContext, AssistantViewType, } from 'app/domains/assistant';
import { useActiveDocument, useDocumentDrawer } from 'shared/domains/documents';
import * as Sentry from 'shared/domains/sentry';
import { Context } from './Context';
import { useAssistantChatAsync } from './useAssistantChatAsync';
import { useAssistantSuggestions } from './useAssistantSuggestions';
import { useCacheOperations } from './useCacheOperations';
import { useCreateDocumentChat } from './useCreateDocumentChat';
import { useDeleteDocumentChat } from './useDeleteDocumentChat';
import { useGetAvailableAssistantCredits } from './useGetAvailableAssistantCredits';
import { useGetLastDocumentChat } from './useGetLastDocumentChat';
export function Provider(props) {
    const { children } = props;
    const { id: documentId } = useActiveDocument();
    const { openDrawer, closeDrawer, isDrawerOpen } = useDocumentDrawer();
    const { addToast } = useToast();
    const { availableCredits, loading: loadingCredits } = useGetAvailableAssistantCredits();
    const { thread, chatId, loading: loadingThread, polling: pollingThread, resolved, resolutionStatus, } = useGetLastDocumentChat();
    const { addTemporaryMessages, clearTemporaryItems } = useCacheOperations();
    const [assistantContext, setAssistantContext] = useState(AssistantContext.Document);
    const [view, setView] = useState(AssistantViewType.Dashboard);
    const [selectedText, setSelectedText] = useState('');
    const [createNewChat, { loading: creatingNewChat }] = useCreateDocumentChat();
    const [deleteChat, { loading: deletingChat }] = useDeleteDocumentChat();
    const [promptAssistant, { loading: promptingAssistant }] = useAssistantChatAsync();
    const isAssistantDrawerOpen = isDrawerOpen('app:assistant');
    const [firstRender, setFirstRender] = useState(true);
    const { suggestions, clearSuggestions, fetchingSuggestions, requestSuggestions } = useAssistantSuggestions();
    const clearThread = () => {
        deleteChat(chatId)
            .then(() => setView(AssistantViewType.Dashboard))
            .catch(error => {
            Sentry.captureException(error);
            addToast({
                title: t({
                    message: 'We could not delete the conversation. Try again',
                    comment: 'Document assistant - error when trying to delete the current chat ',
                }),
                appearance: 'destructive',
            });
        });
    };
    // show the last active conversation when the assistant drawer is opened only on the first render
    useEffect(() => {
        if (firstRender && thread.length > 0) {
            setView(AssistantViewType.Chat);
            setFirstRender(false);
        }
    }, [thread, firstRender]);
    const addComment = (input, options) => __awaiter(this, void 0, void 0, function* () {
        const { type, message } = input;
        const { onCompleted } = options || {};
        setView(AssistantViewType.Chat);
        const handlePrompt = (id) => __awaiter(this, void 0, void 0, function* () {
            addTemporaryMessages({
                message,
                type,
                chatId: id,
            });
            yield promptAssistant({ selection: selectedText, type, chatId: id, message }).then(() => {
                if (onCompleted) {
                    onCompleted();
                }
            });
            clearTemporaryItems({ chatId: id });
        });
        // when type is null, it means that it is a follow-up question from the chat
        // therefore we should not create a new chat, just send the prompt
        if (!type && chatId) {
            return handlePrompt(chatId);
        }
        return createNewChat()
            .then(newChatId => {
            if (!newChatId) {
                Sentry.captureMessage('Document assistant returned no chat id after creating a new document chat');
                return;
            }
            handlePrompt(newChatId);
        })
            .catch(error => {
            Sentry.captureException(error);
            addToast({
                title: t({
                    message: 'We could not initiate a new chat. Try again later.',
                    comment: 'Document assistant - error when creating a new chat ',
                }),
                appearance: 'destructive',
            });
            setView(AssistantViewType.Dashboard);
        });
    });
    const handleSuggestions = () => {
        setView(AssistantViewType.Suggestions);
        requestSuggestions();
    };
    const openAssistantDrawer = useCallback((context) => {
        openDrawer('app:assistant');
        setAssistantContext(context);
    }, [openDrawer]);
    const closeAssistantDrawer = () => {
        closeDrawer('app:assistant');
        setSelectedText('');
        assistantCache.update({ selectionRange: null, selectionText: '' });
    };
    const changeContext = (context) => setAssistantContext(context);
    const updateView = (newView) => setView(newView);
    useEffect(() => {
        assistantCache.setupForDocument(documentId);
        const onPleditorTextSelected = (assistant) => {
            const { selectionText } = assistant;
            if (!isAssistantDrawerOpen) {
                openAssistantDrawer(AssistantContext.Selection);
            }
            else {
                setAssistantContext(AssistantContext.Selection);
            }
            setView(AssistantViewType.Dashboard);
            setSelectedText(selectionText);
        };
        assistantCache.onEvent('pleditor:on-text-selected', onPleditorTextSelected);
        return () => {
            assistantCache.off(onPleditorTextSelected);
        };
    }, [documentId, isAssistantDrawerOpen, openAssistantDrawer]);
    const value = {
        context: assistantContext,
        selectedText,
        openAssistantDrawer,
        closeAssistantDrawer,
        changeContext,
        thread,
        addComment,
        clearThread,
        loading: promptingAssistant || pollingThread || creatingNewChat || deletingChat,
        initiating: loadingThread || loadingCredits,
        creating: creatingNewChat,
        clearing: deletingChat,
        hasCredits: availableCredits > 0,
        resolved,
        resolutionStatus,
        view,
        updateView,
        suggestions,
        requestSuggestions: handleSuggestions,
        fetchingSuggestions,
        clearSuggestions,
    };
    return <Context.Provider value={value}>{children}</Context.Provider>;
}
